import { either, taskEither } from 'fp-ts';
import { errorToReactLeft, pipe } from 'utils/fp';
import { listModelTypesTE } from 'api/models/listModelTypes';
import { listAppDatasetsTE } from 'api/datasets/listDatasets';
import { createModelTE, CreateModelBodyType } from 'api/models/createModel';
import { trainModelTE, TrainModelBodyType } from 'api/models/trainModel';
import { trainModelTimeEstimateTE, TrainModelTimeEstimateBodyType } from 'api/models/trainModelTimeEstimate';
import { updateModelTE, UpdateModelBodyType } from 'api/models/updateModel';
import { updateModelIdTE, UpdateModelIdType } from 'api/models/updateModelId';
import { fetchAppTE } from 'api/apps/fetchApp';
import { listModelsByAppTE } from 'api/models/listModelsByApp';
import { listWorkflowsByAppTE } from 'api/workflows/listWorkflowsByApp';
import { getCollaboratorsTE } from 'api/users';
import { displayError } from 'api/apiUtils';

export const modelFormApiEffects = {
  fetchModelTypeFieldsTE: () =>
    pipe(
      listModelTypesTE({}, errorToReactLeft),
      taskEither.map((resp) => resp.model_types),
    ),
  fetchAppDatasetsTE: (appId: string, userId: string) =>
    pipe(
      listAppDatasetsTE({ appId, userId }, errorToReactLeft),
      taskEither.map((resp) => resp.datasets),
    ),
  createModelTE: (appId: string, userId: string, body: CreateModelBodyType) =>
    pipe(
      createModelTE({ appId, userId, body }),
      taskEither.map((resp) => resp.model),
    ),
  fetchTrainModelTimeEstimate: (appId: string, userId: string, modelId: string, body: TrainModelTimeEstimateBodyType) =>
    pipe(
      trainModelTimeEstimateTE({ userOrOrgId: userId, appId, modelId, body }),
      taskEither.map((resp) => resp.training_time_estimates),
    ),
  trainModelTE: (appId: string, userId: string, modelId: string, body: TrainModelBodyType) =>
    pipe(
      trainModelTE({ userOrOrgId: userId, appId, modelId, body }),
      taskEither.map((resp) => resp.model),
    ),
  updateModelTE: (appId: string, userId: string, body: UpdateModelBodyType) =>
    pipe(
      updateModelTE({ userId, appId, body }),
      taskEither.map((resp) => resp.models),
    ),
  updateModelId: (appId: string, userId: string, body: UpdateModelIdType) =>
    pipe(
      updateModelIdTE({ userId, appId, body }),
      taskEither.map((resp) => resp.models),
    ),
  fetchAppTE: (appId: string, userId: string) =>
    pipe(
      fetchAppTE({ userId, appId }),
      taskEither.map((resp) => resp.app),
    ),
  listModelsByAppTE: (appId: string, userId: string, params: string) =>
    pipe(
      listModelsByAppTE({ userOrOrgId: userId, appId, params }),
      taskEither.map((resp) => resp.models),
    ),
  listWorkflowsByAppTE: (appId: string, userId: string) =>
    pipe(
      listWorkflowsByAppTE({ userOrOrgId: userId, appId }),
      taskEither.map((resp) => resp.workflows),
    ),
  getCollaboratorsTE: (appId: string, userId: string, params: string) =>
    pipe(
      getCollaboratorsTE({ appId, userId, params }),
      taskEither.map((resp) => resp.collaborators),
    ),
  createModelOnly: async (appId: string, userId: string, createModelBody: CreateModelBodyType) => {
    const modelResponse = await modelFormApiEffects.createModelTE(appId as string, userId as string, createModelBody)();

    return pipe(
      modelResponse,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return false;
        },
        () => {
          return true;
        },
      ),
    );
  },
  trainModelOnly: async (appId: string, userId: string, modelId: string, trainModelBody: TrainModelBodyType) => {
    const trainingResult = await modelFormApiEffects.trainModelTE(appId as string, userId as string, modelId, trainModelBody)();
    return pipe(
      trainingResult,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return false;
        },
        (modelResp) => !!modelResp.id,
      ),
    );
  },
  createAndTrainModel: async (appId: string, userId: string, createModelBody: CreateModelBodyType, trainModelBody: TrainModelBodyType) => {
    // Used by hugging face and model upload forms
    const modelResponse = await modelFormApiEffects.createModelTE(appId as string, userId as string, createModelBody)();

    return pipe(
      modelResponse,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return Promise.resolve(false);
        },
        async (model) => {
          const trainingResult = await modelFormApiEffects.trainModelTE(appId as string, userId as string, model.id, trainModelBody)();
          return pipe(
            trainingResult,
            either.fold(
              (error) => {
                displayError(error.status.details, error.status.description);
                return false;
              },
              (modelResp) => !!modelResp.id,
            ),
          );
        },
      ),
    );
  },
  updateAndTrainModelTE: async (
    appId: string,
    userId: string,
    body: UpdateModelBodyType,
    updateIdBody?: UpdateModelIdType,
    trainModelBody?: TrainModelBodyType,
  ) => {
    if (updateIdBody) {
      await modelFormApiEffects.updateModelId(appId, userId, updateIdBody)();
    }

    if (trainModelBody && body.models[0].id) {
      const trainModelResult = await modelFormApiEffects.trainModelTE(appId, userId, body.models[0].id, trainModelBody)();

      return pipe(
        trainModelResult,
        either.fold(
          (error) => {
            displayError(error.status.details, error.status.description);
            return false;
          },
          () => true,
        ),
      );
    }

    return false;
  },
  fetchTimeEstimate: async (
    appId: string,
    userId: string,
    body: UpdateModelBodyType,
    inputCount: number,
    updateIdBody?: UpdateModelIdType,
    trainModelBody?: TrainModelBodyType,
  ) => {
    if (updateIdBody) {
      await modelFormApiEffects.updateModelId(appId, userId, updateIdBody)();
    }

    if (trainModelBody && body.models[0].id) {
      const trainModelEstimateBody = { ...trainModelBody, estimated_input_count: inputCount };
      const trainModelResult = await modelFormApiEffects.fetchTrainModelTimeEstimate(appId, userId, body.models[0].id, trainModelEstimateBody)();

      return pipe(
        trainModelResult,
        either.fold(
          () => [],
          (resp) => resp,
        ),
      );
    }

    return [];
  },
  fetchAppCollaborators: async (appId: string, userId: string) => {
    let page = 1;
    const perPage = 50;

    let appCollaborator: CF.API.Collaborators.Collaborator[] = [];
    let appCollaborators: CF.API.Collaborators.Collaborator[] = [];
    const params = `page=${page}&${perPage}`;

    do {
      const collaboratorE = await modelFormApiEffects.getCollaboratorsTE(userId, appId, params)();
      appCollaborator = pipe(
        collaboratorE,
        either.fold(
          () => [],
          (collaborators) => collaborators,
        ),
      );
      appCollaborators = appCollaborator.concat(appCollaborator);
      page += 1;
    } while (appCollaborator.length === perPage);

    return appCollaborators;
  },
};
